import { useMemo } from 'react';
import { useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';

interface UseContestCardCountdownDisplayProps {
  contest: Contest | MyUserContest;
}

export const useContestCardCountdownDisplay = ({
  contest,
}: UseContestCardCountdownDisplayProps) => {
  const theme = useMantineTheme();
  const { t } = useTranslation('contest');
  const contestDates = useMemo(() => {
    const isEntryDeadlinePending = contest?.is_entry_deadline_pending;
    const lockDate = 'pick_lock_date' in contest ? contest.pick_lock_date : contest.start_date;
    const startDate = contest.start_date;
    const settledDate = 'settled_date' in contest ? contest.settled_date : null;
    const endDate = 'end_date' in contest ? contest.end_date : null;
    let entryLockDate: Date | null = null;
    if (!isEntryDeadlinePending) {
      entryLockDate = 'lock_date' in contest ? contest.lock_date : contest.start_date;
    }

    return {
      lockDate,
      startDate,
      settledDate,
      endDate,
      lockDateMs: lockDate ? new Date(lockDate).getTime() : null,
      startDateMs: startDate ? new Date(startDate).getTime() : null,
      entryLockDate,
      entryLockDateMs: entryLockDate ? new Date(entryLockDate).getTime() : null,
    };
  }, [contest]);

  const getCountdownColor = (dateMs: number | null) => {
    if (!dateMs) return theme.colors.gray[6];

    const currentTimeMs = new Date().getTime();
    const isPastDate = dateMs < currentTimeMs;
    if (isPastDate) return theme.colors.gray[6];

    const hoursUntilDate = (dateMs - currentTimeMs) / (1000 * 60 * 60);
    const hasAlerts = (contest as MyUserContest)?.alerts?.values?.length;

    return hoursUntilDate < 24 && hasAlerts ? theme.colors.orange[4] : theme.colors.gray[6];
  };

  const getLockTimeColor = () => getCountdownColor(contestDates.lockDateMs);
  const getScheduledCountdownColor = () => getCountdownColor(contestDates.startDateMs);
  const getEntryLockTimeColor = () => getCountdownColor(contestDates.entryLockDateMs);

  const getLockTimeText = () => {
    if (!contestDates.lockDateMs) return '';

    const currentTimeMs = new Date().getTime();
    const isPastDate = contestDates.lockDateMs < currentTimeMs;

    return isPastDate
      ? `${t('common.contestCard.header.locked')}: `
      : `${t('common.contestCard.header.locks')}: `;
  };

  const getFinalizedDate = () => contestDates.settledDate || contestDates.endDate;

  return {
    contestDates,
    getLockTimeColor,
    getScheduledCountdownColor,
    getLockTimeText,
    getFinalizedDate,
    getEntryLockTimeColor,
  };
};
